/**
 * PromptEditor — editable prompt template with preview, model settings,
 * and save/reset controls.
 *
 * Loads the current template (override or default) via the render-prompt
 * endpoint. Operators can edit the template, adjust model settings, preview
 * the rendered prompt with actual document variables, and save as a
 * per-article override.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useArticleContext } from '../ArticleContext';
import { blogRunsService } from '../../../services/blogRunsService';

interface PromptEditorProps {
  stageId: string;
}

type PromptData = {
  template: string;
  rendered_prompt: string;
  variables: Record<string, any>;
  model: string;
  temperature: number | null;
  max_tokens: number | null;
  source: 'prompt_instances' | 'pipeline_config';
};

export function PromptEditor({ stageId }: PromptEditorProps) {
  const { run, refreshDocument, refreshArticles } = useArticleContext();
  const runId = run?.run_id;

  const [promptData, setPromptData] = useState<PromptData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable fields (local draft)
  const [draftTemplate, setDraftTemplate] = useState('');
  const [draftModel, setDraftModel] = useState('');
  const [draftTemperature, setDraftTemperature] = useState('');
  const [draftMaxTokens, setDraftMaxTokens] = useState('');

  // Preview state
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Track if user has made changes
  const [dirty, setDirty] = useState(false);

  // Load prompt data from backend
  const loadPrompt = useCallback(async () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await blogRunsService.renderPrompt(runId, stageId);
      setPromptData(data);
      setDraftTemplate(data.template);
      setDraftModel(data.model || '');
      setDraftTemperature(data.temperature != null ? String(data.temperature) : '');
      setDraftMaxTokens(data.max_tokens != null ? String(data.max_tokens) : '');
      setPreviewText(data.rendered_prompt);
      setDirty(false);
      setSaveMessage(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load prompt');
    } finally {
      setLoading(false);
    }
  }, [runId, stageId]);

  // Load on mount
  useEffect(() => {
    void loadPrompt();
  }, [loadPrompt]);

  // Track changes
  const handleTemplateChange = useCallback((value: string) => {
    setDraftTemplate(value);
    setDirty(true);
    setPreviewText(null);
  }, []);

  // Preview: save draft as override, re-render, then show
  const handlePreview = useCallback(async () => {
    if (!runId) return;
    setPreviewLoading(true);
    try {
      // Temporarily save the draft so the render endpoint uses it
      await blogRunsService.savePromptOverride(runId, stageId, {
        prompt_template: draftTemplate,
        model: draftModel || undefined,
        temperature: draftTemperature ? parseFloat(draftTemperature) : undefined,
        max_tokens: draftMaxTokens ? parseInt(draftMaxTokens, 10) : undefined,
      });
      const data = await blogRunsService.renderPrompt(runId, stageId);
      setPreviewText(data.rendered_prompt);
    } catch (e: any) {
      setError(e?.message || 'Failed to preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [runId, stageId, draftTemplate, draftModel, draftTemperature, draftMaxTokens]);

  // Save override
  const handleSave = useCallback(async () => {
    if (!runId) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await blogRunsService.savePromptOverride(runId, stageId, {
        prompt_template: draftTemplate,
        model: draftModel || undefined,
        temperature: draftTemperature ? parseFloat(draftTemperature) : undefined,
        max_tokens: draftMaxTokens ? parseInt(draftMaxTokens, 10) : undefined,
      });
      setDirty(false);
      setSaveMessage('Saved');
      // Refresh to update source indicator
      const data = await blogRunsService.renderPrompt(runId, stageId);
      setPromptData(data);
      setPreviewText(data.rendered_prompt);
    } catch (e: any) {
      setSaveMessage(`Error: ${e?.message || 'Failed to save'}`);
    } finally {
      setSaving(false);
    }
  }, [runId, stageId, draftTemplate, draftModel, draftTemperature, draftMaxTokens]);

  // Reset to default
  const handleReset = useCallback(async () => {
    if (!runId) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await blogRunsService.clearPromptOverride(runId, stageId);
      setSaveMessage('Reset to default');
      await loadPrompt();
    } catch (e: any) {
      setSaveMessage(`Error: ${e?.message || 'Failed to reset'}`);
    } finally {
      setSaving(false);
    }
  }, [runId, stageId, loadPrompt]);

  if (loading) {
    return <div className="text-xs text-gray-400 py-2">Loading prompt...</div>;
  }
  if (error) {
    return <div className="text-xs text-red-500 py-2">{error}</div>;
  }
  if (!promptData) {
    return null;
  }

  const isOverride = promptData.source === 'prompt_instances';

  return (
    <div className="space-y-3">
      {/* Source indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Template source:</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${isOverride ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
            {isOverride ? 'Custom (this article)' : 'Default'}
          </span>
        </div>
        {saveMessage && (
          <span className={`text-xs ${saveMessage.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
            {saveMessage}
          </span>
        )}
      </div>

      {/* Template editor */}
      <div>
        <label className="text-xs text-gray-500">Prompt template</label>
        <textarea
          className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1.5 text-xs font-mono leading-relaxed"
          rows={12}
          value={draftTemplate}
          onChange={(e) => handleTemplateChange(e.target.value)}
        />
      </div>

      {/* Model settings row */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-500">Model</label>
          <input
            type="text"
            className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-xs"
            value={draftModel}
            onChange={(e) => { setDraftModel(e.target.value); setDirty(true); }}
            placeholder="claude-sonnet-4-6"
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Temperature</label>
          <input
            type="number"
            step="0.1"
            min="0"
            max="1"
            className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-xs"
            value={draftTemperature}
            onChange={(e) => { setDraftTemperature(e.target.value); setDirty(true); }}
          />
        </div>
        <div>
          <label className="text-xs text-gray-500">Max tokens</label>
          <input
            type="number"
            step="100"
            min="100"
            className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-xs"
            value={draftMaxTokens}
            onChange={(e) => { setDraftMaxTokens(e.target.value); setDirty(true); }}
          />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          onClick={handlePreview}
          disabled={previewLoading || saving}
        >
          {previewLoading ? 'Rendering...' : 'Preview'}
        </button>
        <button
          type="button"
          className="px-3 py-1 text-xs rounded text-white hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? 'Saving...' : 'Save Override'}
        </button>
        {isOverride && (
          <button
            type="button"
            className="px-3 py-1 text-xs rounded border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
            onClick={handleReset}
            disabled={saving}
          >
            Reset to Default
          </button>
        )}
      </div>

      {/* Rendered preview */}
      {previewText && (
        <details open={dirty || previewLoading}>
          <summary className="text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-800 py-1">
            Rendered Preview
          </summary>
          <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-96 overflow-y-auto mt-1">
            {previewText}
          </pre>
        </details>
      )}

      {/* Variables reference */}
      <details>
        <summary className="text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-800 py-1">
          Available Variables ({Object.keys(promptData.variables).length})
        </summary>
        <div className="mt-1 bg-gray-50 border rounded p-2 max-h-64 overflow-y-auto">
          {Object.entries(promptData.variables).map(([key, value]) => (
            <div key={key} className="text-xs py-0.5">
              <span className="font-mono text-blue-700">{'{{ '}{key}{' }}'}</span>
              <span className="text-gray-400 ml-2">
                {typeof value === 'string'
                  ? (value.length > 80 ? value.slice(0, 80) + '...' : value)
                  : Array.isArray(value)
                    ? `[${value.length} items]`
                    : typeof value === 'object' && value !== null
                      ? '{...}'
                      : String(value)}
              </span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}
